import { COOKIE_CONSENT_TTL_DAYS, COOKIE_CONSENT_VERSION, KEY_COOKIE_CONSENT } from '../constants';
import type { CookieConsent } from 'interfaces/cookieConsent';
import { getCookie, setCookie } from './cookies';
import { addDaysDate } from './functions';

const isCookieConsent = (value: unknown): value is CookieConsent => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as CookieConsent;

  return (
    candidate.version === COOKIE_CONSENT_VERSION &&
    candidate.necessary &&
    typeof candidate.analytics === 'boolean' &&
    typeof candidate.marketing === 'boolean'
  );
};

const parseCookieConsent = (rawValue: unknown): CookieConsent | null => {
  if (isCookieConsent(rawValue)) {
    return rawValue;
  }

  if (typeof rawValue !== 'string' || rawValue.length === 0) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);

    if (!isCookieConsent(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const getCookieConsent = (): CookieConsent | null => {
  return parseCookieConsent(getCookie(KEY_COOKIE_CONSENT));
};

export const setCookieConsent = (consent: CookieConsent): void => {
  const expiresAt = addDaysDate(COOKIE_CONSENT_TTL_DAYS);

  setCookie(KEY_COOKIE_CONSENT, JSON.stringify(consent), expiresAt, {
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
  });
};

export const saveCookieConsent = (consent: Pick<CookieConsent, 'analytics' | 'marketing'>): void => {
  setCookieConsent({
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    analytics: consent.analytics,
    marketing: consent.marketing,
  });
};

export const acceptAllCookieConsent = (): void => {
  saveCookieConsent({
    analytics: true,
    marketing: true,
  });
};
